<template>
  <div class="actions-tab">
    <div class="actions-tab-header">
      <div class="actions-counts">
        <span class="count-chip"
          >{{ closedCount }}/{{ totalCount }} closed</span
        >
        <span class="count-chip">{{ correctiveCount }} corrective</span>
        <span class="count-chip">{{ preventativeCount }} preventative</span>
      </div>
      <button v-if="!isReadOnly" class="btn-add-action" @click="addRow">
        <svg viewBox="0 0 24 24">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        Add Action
      </button>
    </div>

    <dx-data-grid
      ref="actionsGrid"
      :data-source="getActionsDataSource"
      key-expr="_id"
      :height="getGridHeight"
      :show-borders="true"
      :word-wrap-enabled="true"
      :show-row-lines="true"
      :no-data-text="noDataText"
      :column-auto-width="false"
      :row-alternation-enabled="true"
      :column-chooser="{ enabled: false }"
      :pager="{ visible: false }"
      :columns="getColumns"
      :scrolling="{
        mode: 'virtual',
        rowRenderingMode: 'virtual',
        preloadEnabled: true,
      }"
      @toolbar-preparing="onToolbarPreparing"
      @content-ready="onContentReady"
      @editor-preparing="onEditorPreparing"
      @row-inserted="onDataChanged"
      @row-updated="onDataChanged"
      @row-removed="onDataChanged"
    >
      <DxKeyboardNavigation
        :edit-on-key-press="true"
        :enter-key-action="'startEdit'"
        :enter-key-direction="'none'"
        :enabled="true"
      />

      <dx-editing
        :allow-updating="!isReadOnly"
        :allow-deleting="!isReadOnly"
        mode="cell"
        new-row-position="last"
      />
      <dx-paging :enabled="false" />

      <template #typeCell="{ data }">
        <span
          class="type-badge"
          :class="
            data.value === 'Corrective'
              ? 'type-corrective'
              : 'type-preventative'
          "
          >{{ data.value }}</span
        >
      </template>

      <template #statusCell="{ data }">
        <span class="status-badge" :class="statusClass(data.value)">{{
          data.value
        }}</span>
      </template>
    </dx-data-grid>
  </div>
</template>

<script>
import ArrayStore from "devextreme/data/array_store";
import DxDataGrid, {
  DxEditing,
  DxPaging,
  DxKeyboardNavigation,
} from "devextreme-vue/data-grid";
import { mapGetters } from "vuex";
import dataGridMixin from "../../_mixins/data-grid.mixin";
import ColumnWidth from "@/_helpers/data-grid/ColumnWidth";

export default {
  mixins: [dataGridMixin],

  components: {
    DxDataGrid,
    DxEditing,
    DxPaging,
    DxKeyboardNavigation,
  },

  props: {
    actions: {
      type: Array,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      actionsDataSource: new ArrayStore({
        key: "_id",
        data: [],
      }),
      typeOptions: ["Corrective", "Preventative"],
      statusOptions: ["Open", "In Progress", "Closed"],
      headerFormHeight: 0,
      actionsHeaderHeight: 0,
    };
  },

  computed: {
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),

    getGridHeight() {
      if (!this.gridHeight) return undefined;
      return (
        this.gridHeight - 167 - this.headerFormHeight - this.actionsHeaderHeight
      );
      // 167 = sum of fixed heights between the scroll-view and the grid:
      //   10  transaction-screen padding-top
      //   55  gc-header-toolbar (45px content + 10px padding-bottom)
      //   10  complaint-content-tabs margin-top
      //   40  complaint-tab-toolbar
      //   16  complaint-tab-content padding-top
      //   10  actions-tab-header margin-bottom
      //   16  complaint-tab-content padding-bottom
      //   10  transaction-screen padding-bottom
    },

    getActionsDataSource() {
      return this.actionsDataSource._array;
    },

    totalCount() {
      return this.actionsDataSource._array.length;
    },
    closedCount() {
      return this.actionsDataSource._array.filter(function (a) {
        return a.Status === "Closed";
      }).length;
    },
    correctiveCount() {
      return this.actionsDataSource._array.filter(function (a) {
        return a.Type === "Corrective";
      }).length;
    },
    preventativeCount() {
      return this.actionsDataSource._array.filter(function (a) {
        return a.Type === "Preventative";
      }).length;
    },
    noDataText() {
      return this.isReadOnly
        ? "No corrective or preventative actions recorded."
        : "No actions yet — click Add Action to begin.";
    },

    getColumns() {
      var self = this;
      return [
        {
          dataField: "Description",
          caption: "Description",
          minWidth: 200,
          cssClass: "edit-cell-textbox-field",
          editorOptions: { maxLength: 1000 },
          setCellValue: function (newData, value, currentRowData) {
            newData.Description = value;
            if (!self.isReadOnly && !currentRowData.Description && value) {
              self.actionsDataSource.insert({
                _id: self._idCounter++,
                SequenceNumber: 0,
                RecordId: null,
                Status: "Open",
                Type: "Corrective",
                FollowUpReviewed: false,
                Description: null,
                Owner: null,
                DueDate: null,
                TargetFollowUpDate: null,
              });
              setTimeout(function () {
                var grid = self.$refs.actionsGrid.instance;
                grid.saveEditData();
                grid.refresh(true);
                self.$nextTick(function () {
                  grid.updateDimensions();
                });
              }, 100);
            }
          },
        },
        {
          dataField: "Type",
          caption: "Type",
          width: 110,
          cssClass: "edit-cell-textbox-field",
          cellTemplate: "typeCell",
          lookup: {
            dataSource: self.typeOptions,
          },
        },
        {
          dataField: "Owner",
          caption: "Owner",
          width: 150,
          cssClass: "edit-cell-textbox-field",
          editorOptions: { maxLength: 100 },
        },
        {
          dataField: "DueDate",
          caption: "Due Date",
          dataType: "date",
          format: "dd MMM yyyy",
          cssClass: "edit-cell-textbox-field",
          minWidth: ColumnWidth.Date.minWidth,
          width: ColumnWidth.Date.width,
        },
        {
          dataField: "Status",
          caption: "Status",
          cssClass: "edit-cell-textbox-field",
          cellTemplate: "statusCell",
          minWidth: ColumnWidth.Status.minWidth,
          width: ColumnWidth.Status.width,
          lookup: {
            dataSource: self.statusOptions,
          },
        },
        {
          dataField: "FollowUpReviewed",
          caption: "Follow-up Reviewed",
          dataType: "boolean",
          width: 130,
        },
        {
          dataField: "TargetFollowUpDate",
          caption: "Target Follow-up Date",
          dataType: "date",
          format: "dd MMM yyyy",
          cssClass: "edit-cell-textbox-field",
          minWidth: ColumnWidth.Date.minWidth + 40,
          width: ColumnWidth.Date.width + 40,
          setCellValue: function (newData, value) {
            newData.TargetFollowUpDate = value;
          },
        },
        {
          type: "buttons",
          width: 70,
          visible: !self.isReadOnly,
          buttons: [
            {
              icon: "copy",
              hint: "Copy row",
              onClick: function (e) {
                self.onCopyRow(e);
              },
            },
            { name: "delete" },
          ],
        },
      ];
    },
  },

  created() {
    this._idCounter = 1;
    var self = this;
    self.actions.forEach(function (a) {
      self.actionsDataSource.insert(Object.assign({}, a, { _id: self._idCounter++ }));
    });
  },

  mounted() {
    this.$nextTick(
      function () {
        var headerForm = document.querySelectorAll(".gc-header-form");
        if (headerForm[0]) {
          this.headerFormHeight = headerForm[0].clientHeight;
        }
        var actionsHeader = this.$el.querySelector(".actions-tab-header");
        if (actionsHeader) {
          this.actionsHeaderHeight = actionsHeader.clientHeight;
        }
      }.bind(this),
    );
  },

  methods: {
    onToolbarPreparing(e) {
      e.toolbarOptions.visible = false;
    },

    onContentReady(e) {
      this.addGridNoResults(e, undefined);
    },

    addRow() {
      var self = this;
      var newId = self._idCounter++;
      self.actionsDataSource.insert({
        _id: newId,
        SequenceNumber: 0,
        RecordId: null,
        Status: "Open",
        Type: "Corrective",
        FollowUpReviewed: false,
        Description: null,
        Owner: null,
        DueDate: null,
        TargetFollowUpDate: null,
      });
      setTimeout(function () {
        var grid = self.$refs.actionsGrid.instance;
        grid.saveEditData();
        grid.refresh(true);
        self.$nextTick(function () {
          grid.updateDimensions();
          var rows = grid.getVisibleRows();
          for (var i = 0; i < rows.length; i++) {
            if (rows[i].data && rows[i].data._id === newId) {
              grid.editCell(rows[i].rowIndex, "Description");
              break;
            }
          }
        });
      }, 100);
    },

    onCopyRow(e) {
      var self = this;
      var src = e.row.data;
      var newId = self._idCounter++;
      self.actionsDataSource.insert({
        _id: newId,
        SequenceNumber: 0,
        RecordId: null,
        Status: src.Status || "Open",
        Type: src.Type || "Corrective",
        FollowUpReviewed: src.FollowUpReviewed || false,
        Description: src.Description || null,
        Owner: src.Owner || null,
        DueDate: src.DueDate || null,
        TargetFollowUpDate: src.TargetFollowUpDate || null,
      });
      setTimeout(function () {
        var grid = self.$refs.actionsGrid.instance;
        grid.saveEditData();
        grid.refresh(true);
        self.$nextTick(function () {
          grid.updateDimensions();
          var rows = grid.getVisibleRows();
          for (var i = 0; i < rows.length; i++) {
            if (rows[i].data && rows[i].data._id === newId) {
              grid.editCell(rows[i].rowIndex, "Description");
              break;
            }
          }
        });
      }, 100);
    },

    onEditorPreparing(e) {
      if (e.parentType !== "dataRow") return;

      if (e.dataField === "Description") {
        var self = this;
        var prevOnKeyDown = e.editorOptions.onKeyDown;
        e.editorOptions.onKeyDown = function (args) {
          if (prevOnKeyDown) prevOnKeyDown(args);
          if (args.event.key !== "Tab" || args.event.shiftKey) return;
          if (!e.row) return;

          var arr = self.actionsDataSource._array;
          if (!arr.length) return;
          var lastItem = arr[arr.length - 1];
          // Only act when we're on the last row and it already has a description
          // (the empty-row case is already handled by setCellValue)
          if (lastItem._id !== e.row.key || !lastItem.Description) return;

          setTimeout(function () {
            var arr2 = self.actionsDataSource._array;
            var last = arr2[arr2.length - 1];
            // Skip if setCellValue already added a blank trailing row
            if (last && !last.Description) return;

            var newId = self._idCounter++;
            self.actionsDataSource.insert({
              _id: newId,
              SequenceNumber: 0,
              RecordId: null,
              Status: "Open",
              Type: "Corrective",
              FollowUpReviewed: false,
              Description: null,
              Owner: null,
              DueDate: null,
              TargetFollowUpDate: null,
            });
            var grid = self.$refs.actionsGrid.instance;
            setTimeout(function () {
              grid.saveEditData();
              grid.refresh(true);
              self.$nextTick(function () {
                grid.updateDimensions();
              });
            }, 100);
          }, 150);
        };
      }

      if (e.dataField === "DueDate") {
        e.editorOptions = Object.assign({}, e.editorOptions, {
          type: "date",
          displayFormat: "dd MMM yyyy",
        });
      }

      if (e.dataField === "TargetFollowUpDate") {
        e.editorOptions = Object.assign({}, e.editorOptions, {
          type: "date",
          displayFormat: "dd MMM yyyy",
          showClearButton: true,
        });
      }
    },

    onDataChanged() {
      var self = this;
      self.actions.splice(0, self.actions.length);
      self.actionsDataSource._array.forEach(function (a) {
        if (!a.Description) return;
        self.actions.push({
          RecordId: a.RecordId || null,
          SequenceNumber: a.SequenceNumber || 0,
          Description: a.Description || "",
          Type: a.Type || "Corrective",
          Owner: a.Owner || "",
          DueDate: a.DueDate || null,
          Status: a.Status || "Open",
          FollowUpReviewed: a.FollowUpReviewed || false,
          TargetFollowUpDate: a.TargetFollowUpDate || null,
        });
      });
    },

    commitEdits() {
      var grid = this.$refs.actionsGrid.instance;
      var self = this;

      var syncToParent = function () {
        self.actions.splice(0, self.actions.length);
        self.actionsDataSource._array.forEach(function (a) {
          if (!a.Description) return;
          self.actions.push({
            RecordId: a.RecordId || null,
            SequenceNumber: a.SequenceNumber || 0,
            Description: a.Description || "",
            Type: a.Type || "Corrective",
            Owner: a.Owner || "",
            DueDate: a.DueDate || null,
            Status: a.Status || "Open",
            FollowUpReviewed: a.FollowUpReviewed || false,
            TargetFollowUpDate: a.TargetFollowUpDate || null,
          });
        });
      };

      syncToParent();

      if (!grid.hasEditData()) return Promise.resolve();

      return grid.saveEditData().then(function () {
        syncToParent();
      });
    },

    statusClass(status) {
      if (status === "Open") return "status-open";
      if (status === "In Progress") return "status-inprogress";
      if (status === "Closed") return "status-closed";
      return "";
    },
  },
};
</script>

<style lang="scss">
.actions-tab .dx-datagrid-header-panel {
  display: none;
}
</style>

<style lang="scss" scoped>
.dx-card {
  box-shadow: none;
  -webkit-box-shadow: none;
}

.actions-tab {
  .actions-tab-header {
    display: flex;
    align-items: center;
    padding: 0px;
    margin-bottom: 10px;

    .actions-counts {
      display: flex;
      gap: 6px;
      align-items: center;
    }

    .btn-add-action {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 12px;
      border-radius: 2px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      background: #35baf6;
      border: 1px solid #2a95c5;
      color: #fff;
      transition: background 0.1s;
      user-select: none;

      &:hover {
        background: #2aa8e0;
      }

      svg {
        width: 13px;
        height: 13px;
        stroke: currentColor;
        fill: none;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
        flex-shrink: 0;
      }
    }
  }

  .count-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    color: #667085;
    background: #f9fafb;
    border: 1px solid #eaecf0;
    border-radius: 9999px;
    padding: 2px 10px;
    white-space: nowrap;
  }

  .type-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;

    &.type-corrective {
      background: #fff3cd;
      color: #856404;
      border: 1px solid #ffc107;
    }

    &.type-preventative {
      background: #cce5ff;
      color: #004085;
      border: 1px solid #b8daff;
    }
  }

  .status-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;

    &.status-open {
      background: #e3f2fd;
      color: #1565c0;
      border: 1px solid #90caf9;
    }

    &.status-inprogress {
      background: #fff8e1;
      color: #e65100;
      border: 1px solid #ffcc02;
    }

    &.status-closed {
      background: #e8f5e9;
      color: #2e7d32;
      border: 1px solid #a5d6a7;
    }
  }
}
</style>
